import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { AgrI18nSharedModule, LocaleService } from '@next/core-lib/i18n';

import {
  DownyMildewObservationRegistration,
  DownyMildewObservationSeverity,
} from '../../models/observation.models';
import { buildCropFieldHistoryTables, CropFieldHistoryTable } from '../../shared/helpers/crop-field-history-table.helper';

export interface ObservationCropFieldInfo {
  id: number;
  name: string;
}

interface ObservationHistoryRow {
  observationId: number;
  cropFieldId: number;
  createdAt: Date;
  observationDate: Date;
  severity: DownyMildewObservationSeverity;
}

type CropFieldObservationTable = CropFieldHistoryTable<ObservationCropFieldInfo, ObservationHistoryRow>;

@Component({
  selector: 'app-observation-history',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatProgressSpinnerModule, MatIconModule, MatButtonModule, AgrI18nSharedModule],
  templateUrl: './observation-history.component.html',
  styleUrl: './observation-history.component.scss',
})
export class ObservationHistoryComponent {
  private readonly localeService = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly registrations = input.required<DownyMildewObservationRegistration[]>();
  public readonly cropFields = input.required<ObservationCropFieldInfo[]>();
  public readonly isLoading = input<boolean>(false);
  public readonly readOnly = input<boolean>(false);
  public readonly removeRegistration = output<{ cropFieldId: number; observationId: number }>();

  protected locale = this.localeService.getLocale();
  protected readonly displayedColumns = computed(() =>
    this.readOnly() ? ['date', 'severity'] : ['date', 'severity', 'actions'],
  );

  protected readonly tables = computed((): CropFieldObservationTable[] => {
    return buildCropFieldHistoryTables(this.cropFields(), this.registrations(), (registration) => this.mapToRow(registration)).map((table) => ({
      ...table,
      rows: [...table.rows].sort((left, right) => right.observationDate.getTime() - left.observationDate.getTime()),
    }));
  });

  constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
  }

  protected onRemoveRegistration(row: ObservationHistoryRow): void {
    if (this.readOnly()) {
      return;
    }

    this.removeRegistration.emit({
      cropFieldId: row.cropFieldId,
      observationId: row.observationId,
    });
  }

  private mapToRow(registration: DownyMildewObservationRegistration): ObservationHistoryRow {
    return {
      observationId: registration.id,
      cropFieldId: registration.cropFieldId,
      createdAt: new Date(registration.createdAt),
      observationDate: new Date(registration.observationDate),
      severity: registration.severity,
    };
  }
}
